import { useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import useAutomationWebSocket from '../../../components/AutomationStudio/hooks/useAutomationWebSocket';
import { useOrganizationServiceUrl } from '../../../hooks/useOrganizationServiceUrl';
import { useSnackbar } from '../../../utils/useSnackbar';
import { AUTOMATION_STUDIO_ROUTES } from '../automationStudioRoutes';
import { markAutomationStudioSessionPreserved } from '../automationStudioSession';
import { AI_FEATURE_KEYS } from '../../../enums/AIModelEnums';
import { getActiveAIModels } from '../../../utils/api/aimodel_apis';
import { updateAutomationInstruction, updateExecutionRecord, saveAutomationInstructionOnly } from '../../../utils/api';
import { createExecutionSession } from '../../../utils/api/executionSession_apis';
import { STATUS } from '../../../utils/api/sharedfunction';
import { getTrialUsage } from '../../../utils/api/trialUsage_apis';
import {
    canConnectAutomationStudioSocket,
} from '../../../utils/trialUsageGuards';
import {
    getWebSocketConnectErrorMessage,
    isWebSocketConnectSuccess,
} from '../../../utils/commonfunctions';
import { buildVariablePayloadForAgentStrict } from '../../../utils/automation/variableUtils';
import { trackUiAction } from '../../../utils/telemetryClient';
import { TelemetryActions } from '../../../enums/TelemetryActionsEnum';
import { resolveDefaultConcreteModel } from '../../../components/Select/llmModelSelector/AIModelRuntimeSelector';
import {
    generationStarted,
    generationStopped,
    markPlanUnsavedFromGeneration,
    replayStarted,
    replayStopped,
    saveStarted,
    saveFailed,
    planSaved,
    instructionsOnlySaved,
    setInstructions,
    navigateToExecution,
    setValidationErrors,
} from '../automationStudioNewSlice';
import {
    selectInstructions,
    selectDynamicResult,
    selectUi,
    selectIsStepValidationValid,
    selectStepValidation,
    selectSavedPlan,
    selectGenerationRun,
    selectInstructionsForExecutionView,
    selectSaveChangeKind,
} from '../automationStudioNewSelectors';
import {
    buildPlanForSave,
    getInstructionPayloadToSave,
} from '../planBuilder';
import useAutomationStudioNewSocketBridge from './useAutomationStudioNewSocketBridge';
import {
    getFailedRecordingStepsForSaveConfirmFromState,
} from '../recordingPlanMarkers';
import { RUN_STATUS, SAVE_OPENED_LINK_TEST_CASE_POPUP } from '../constants';
import {
    buildValidInstructionsPayload,
    getActiveFlatSteps,
    getFlatSteps,
    prepareInstructionsForGeneration,
    resolvePostSessionSaveInstructions,
} from '../planUtils';
import {
    buildInstructionsOnlyLinkPopupData,
    resolveInstructionsOnlyLinkSaveAction,
    INSTRUCTIONS_ONLY_LINK_SAVE_ACTION,
} from '../instructionOnlyLinkSaveUtils';

/** Ignore React click events mistakenly passed as overrideTestCaseId from onClick handlers. */
function resolveReplayTestCaseIdOverride(override) {
    if (override == null) return null;
    if (typeof override === 'string' && override.trim()) return override.trim();
    if (typeof override === 'number' && Number.isFinite(override)) return String(override);
    return null;
}

function parseModelString(modelStr) {
    if (!modelStr) return null;
    try {
        return JSON.parse(modelStr);
    } catch {
        return modelStr;
    }
}

export default function useAutomationStudioNewActions({
    projectId,
    orgId,
    userId,
    testCaseId,
    testCaseKey,
    isTrialOrg,
    canSelectAiModel,
    onOpenSavePopup,
    onOpenConfirmSavePopup,
}) {
    const dispatch = useDispatch();
    const store = useStore();
    const navigate = useNavigate();
    const params = useParams();
    const { showSnackbar } = useSnackbar();

    const instructions = useSelector(selectInstructions);
    const executionInstructions = useSelector(selectInstructionsForExecutionView);
    const generationRun = useSelector(selectGenerationRun);
    const dynamicResult = useSelector(selectDynamicResult);
    const ui = useSelector(selectUi);
    const isStepValidationValid = useSelector(selectIsStepValidationValid);
    const stepValidation = useSelector(selectStepValidation);
    const savedPlan = useSelector(selectSavedPlan);
    const variablesList = useSelector((state) => state.replay?.variablesList || []);
    const variablesListDescriptions = useSelector(
        (state) => state.replay?.variablesListDescriptions || {}
    );

    const activeRunIdRef = useRef(null);
    /** 'save' | 'saveAndReplay' while failed-recording confirm modal is open */
    const pendingSaveAfterConfirmRef = useRef(null);
    const { organizationData } = useOrganizationServiceUrl();
    const { agentServiceUrl } = organizationData || {};

    const wsConfig = useMemo(() => {
        const config = {};
        if (agentServiceUrl) config.url = agentServiceUrl;
        config.beforeConnect = async () => {
            const r = await getTrialUsage();
            const d = r?.status === 'success' && r?.data ? r.data : null;
            if (isTrialOrg) {
                const gate = canConnectAutomationStudioSocket(d);
                if (!gate.allowed) return { success: false, message: gate.message };
            }
            return {
                allowed: true,
                usageData: d
                    ? {
                          credits_used: d.credits_used,
                          credit_limit: d.credit_limit,
                          script_creator_cap: d.script_creator_cap,
                          tokens_per_credit: d.tokens_per_credit,
                      }
                    : undefined,
            };
        };
        config.onCreditLimitReached = (opts) => {
            if (opts?.message) showSnackbar(opts.message, 'error');
        };
        const refresh = () => window.dispatchEvent(new CustomEvent('trialUsageRefresh'));
        config.onScriptCreatorRunComplete = refresh;
        config.onReplayComplete = refresh;
        return config;
    }, [agentServiceUrl, isTrialOrg, showSnackbar]);

    const {
        connect,
        startAutomation: wsStartAutomation,
        stopAutomation,
        markReplayStarting,
        replayAutomation,
        stopReplayAutomation,
        clearAutomationData,
        on,
        isConnected,
    } = useAutomationWebSocket(wsConfig);

    useAutomationStudioNewSocketBridge({ on, isConnected, activeRunIdRef });

    const resolveGenerateModel = useCallback(async () => {
        const selected = ui.selectedAIModel;
        if (selected) return parseModelString(selected);

        if (!canSelectAiModel && !isTrialOrg) {
            return { featureKey: AI_FEATURE_KEYS.AUTOMATION_RECORDING };
        }

        const res = await getActiveAIModels(AI_FEATURE_KEYS.AUTOMATION_RECORDING, {
            trialOrgSkip: isTrialOrg,
            projectId,
        });
        if (res.status === STATUS.SUCCESS) {
            const resolved = resolveDefaultConcreteModel(res.data || []);
            if (resolved?.provider && resolved?.model_key) {
                return { provider: resolved.provider, model_key: resolved.model_key };
            }
        }
        return null;
    }, [ui.selectedAIModel, canSelectAiModel, isTrialOrg, projectId]);

    const resolveReplayModel = useCallback(async () => {
        const selected = ui.selectedAIModel;
        if (selected) return parseModelString(selected);

        const res = await getActiveAIModels(AI_FEATURE_KEYS.REPLAY_FLOW, {
            trialOrgSkip: isTrialOrg,
            projectId,
        });
        if (res.status === STATUS.SUCCESS) {
            const resolved = resolveDefaultConcreteModel(res.data || []);
            if (resolved?.provider && resolved?.model_key) {
                return { provider: resolved.provider, model_key: resolved.model_key };
            }
        }
        return null;
    }, [ui.selectedAIModel, isTrialOrg, projectId]);

    const handleGenerate = useCallback(async () => {
        trackUiAction(TelemetryActions.GENERATE_AI_CLICKED, { projectId });
        const modelForRun = await resolveGenerateModel();
        if (!modelForRun) {
            showSnackbar('Please select an AI Model before generating steps', 'error');
            return;
        }

        if (!instructions.some((inst) => !inst.removedFromEditor && inst.text?.trim())) {
            showSnackbar('Please add at least one instruction before generating steps', 'error');
            return;
        }

        try {
            const connectionResult = await connect();
            if (!isWebSocketConnectSuccess(connectionResult)) {
                showSnackbar(
                    getWebSocketConnectErrorMessage(
                        connectionResult,
                        'Failed to connect to automation service. Please try again.'
                    ),
                    'error'
                );
                return;
            }

            let sessionId = null;
            try {
                const sessionResponse = await createExecutionSession({ mode: 'recorder' });
                if (sessionResponse.status === STATUS.SUCCESS) {
                    sessionId = sessionResponse.data.sessionId;
                }
            } catch (e) {
                console.warn('[AutomationStudioNew] Failed to create execution session', e);
            }

            const uniqueId = sessionId || `${Math.random().toString(36).slice(2, 11)}${Date.now()}`;
            activeRunIdRef.current = uniqueId;

            const executionContext = {
                projectId,
                organizationId: orgId,
                userId,
            };

            const prepared = prepareInstructionsForGeneration(instructions);
            const validInstructions = buildValidInstructionsPayload(prepared);

            const variablePayload = buildVariablePayloadForAgentStrict(
                validInstructions,
                variablesList,
                variablesListDescriptions
            );

            const isRecorderSession =
                modelForRun?.featureKey !== AI_FEATURE_KEYS.REPLAY_FLOW;

            dispatch(
                generationStarted({
                    runId: uniqueId,
                    sessionMode: isRecorderSession ? 'recorder' : 'generate',
                })
            );
            dispatch(setInstructions(prepared));
            dispatch(navigateToExecution());
            clearAutomationData();

            const modelPayload =
                typeof modelForRun === 'string'
                    ? modelForRun
                    : modelForRun.featureKey
                      ? JSON.stringify(modelForRun)
                      : JSON.stringify({
                            provider: modelForRun.provider,
                            model_key: modelForRun.model_key,
                        });

            const startResult = await wsStartAutomation(
                uniqueId,
                validInstructions,
                modelPayload,
                ui.selectedEnvironment,
                executionContext,
                projectId,
                null,
                variablePayload
            );

            if (!startResult?.ok) {
                showSnackbar(startResult?.error || 'Could not start AI generation.', 'error');
                dispatch(generationStopped());
                return;
            }

            dispatch(markPlanUnsavedFromGeneration());
        } catch (error) {
            showSnackbar(`Error starting automation: ${error.message || error}`, 'error');
            dispatch(generationStopped());
        }
    }, [
        dispatch,
        instructions,
        resolveGenerateModel,
        connect,
        wsStartAutomation,
        ui.selectedEnvironment,
        projectId,
        orgId,
        userId,
        variablesList,
        variablesListDescriptions,
        clearAutomationData,
        showSnackbar,
    ]);

    const handleReplay = useCallback(async (overrideTestCaseId) => {
        trackUiAction(TelemetryActions.REPLAY_STARTED_CLICKED, { projectId });
        const studioState = store.getState().automationStudioNew;
        const safeOverride = resolveReplayTestCaseIdOverride(overrideTestCaseId);
        const effectiveTestCaseId =
            safeOverride ?? testCaseId ?? studioState?.savedPlan?.testCaseId ?? null;
        const liveInstructions = studioState?.planDraft?.instructions ?? instructions;

        // Match legacy: use selector value directly for llmId when set.
        let modelForReplay = ui.selectedAIModel || null;
        if (!modelForReplay) {
            const resolved = await resolveReplayModel();
            if (!resolved) {
                showSnackbar(
                    'No AI model configured for Replay. Configure a model in Administration > Configuration.',
                    'error'
                );
                return;
            }
            modelForReplay =
                typeof resolved === 'string'
                    ? resolved
                    : JSON.stringify({
                          provider: resolved.provider,
                          model_key: resolved.model_key,
                      });
        }

        if (!effectiveTestCaseId && !getFlatSteps(liveInstructions).length) {
            showSnackbar('No execution plan available to replay', 'error');
            return;
        }

        if (!effectiveTestCaseId) {
            showSnackbar('Save the execution record to a test case before replay.', 'error');
            return;
        }

        if (!projectId) {
            showSnackbar('Project context is required for replay.', 'error');
            return;
        }

        try {
            clearAutomationData();
            markReplayStarting();

            // Legacy connects and registers WS listeners before emitting replay.
            const connectionResult = await connect();
            if (!isWebSocketConnectSuccess(connectionResult)) {
                showSnackbar(
                    getWebSocketConnectErrorMessage(connectionResult, 'Failed to establish connection'),
                    'error'
                );
                return;
            }

            let replaySessionId = null;
            try {
                const sessionResponse = await createExecutionSession({
                    testcase_id: effectiveTestCaseId || null,
                    mode: 'replay',
                });
                if (sessionResponse.status === STATUS.SUCCESS) {
                    replaySessionId = sessionResponse.data.sessionId;
                }
            } catch (e) {
                console.warn('[AutomationStudioNew] Failed to create replay session', e);
            }

            const execId = replaySessionId || `${Math.random().toString(36).slice(2, 11)}${Date.now()}`;
            activeRunIdRef.current = execId;

            dispatch(replayStarted({ runId: execId }));
            dispatch(navigateToExecution());

            const executionContext = {
                projectId,
                organizationId: orgId,
                userId,
            };

            const options = { stopOnError: true, directExecution: true, useLLMFallback: true };
            const startResult = await replayAutomation(
                null,
                options,
                effectiveTestCaseId,
                execId,
                ui.selectedEnvironment,
                executionContext,
                projectId,
                modelForReplay
            );

            if (!startResult?.ok) {
                showSnackbar(startResult?.error || 'Failed to start replay', 'error');
                dispatch(replayStopped());
            }
        } catch (error) {
            showSnackbar(`Replay failed: ${error.message || error}`, 'error');
            dispatch(replayStopped());
        }
    }, [
        ui.selectedAIModel,
        resolveReplayModel,
        instructions,
        dynamicResult,
        testCaseKey,
        clearAutomationData,
        markReplayStarting,
        connect,
        replayAutomation,
        testCaseId,
        ui.selectedEnvironment,
        projectId,
        orgId,
        userId,
        dispatch,
        showSnackbar,
        store,
    ]);

    const persistSave = useCallback(
        async (targetTestCaseId) => {
            if (!targetTestCaseId) return false;

            const studioState = store.getState().automationStudioNew;
            const liveInstructions = studioState?.planDraft?.instructions ?? [];
            const liveDynamicResult = studioState?.planDraft?.dynamicResult ?? null;
            const instructionsToSave = resolvePostSessionSaveInstructions(
                studioState.generationRun,
                liveInstructions
            );

            dispatch(saveStarted());
            try {
                await updateAutomationInstruction({
                    testCaseId: targetTestCaseId,
                    instruction: getInstructionPayloadToSave(instructionsToSave),
                });

                const planToSave = buildPlanForSave(instructionsToSave, liveDynamicResult, testCaseKey);
                const result = await updateExecutionRecord({
                    testCaseId: targetTestCaseId,
                    plan: planToSave,
                    dynamicResult: planToSave?.metadata?.dynamicResult || planToSave?.dynamicResults,
                });

                if (result?.status === 1) {
                    dispatch(
                        planSaved({
                            testCaseId: targetTestCaseId,
                            testCaseKey,
                            executionRecordPlan: planToSave,
                        })
                    );
                    return true;
                }

                dispatch(saveFailed(result?.error || 'Failed to save execution record'));
                showSnackbar('Failed to save execution record', 'error');
                return false;
            } catch (error) {
                dispatch(saveFailed(error.message || 'Save failed'));
                showSnackbar('Failed to save execution record', 'error');
                return false;
            }
        },
        [dispatch, testCaseKey, showSnackbar, store]
    );

    const persistInstructionsOnly = useCallback(
        async (targetTestCaseId) => {
            if (!targetTestCaseId) return false;

            const studioState = store.getState().automationStudioNew;
            // Live draft — not completedInstructions (that snapshot is frozen at generate time).
            const instructionsToSave = studioState?.planDraft?.instructions ?? [];

            dispatch(saveStarted());
            try {
                const result = await saveAutomationInstructionOnly({
                    testCaseId: targetTestCaseId,
                    instruction: getInstructionPayloadToSave(instructionsToSave),
                });

                if (result?.status === 1) {
                    dispatch(
                        instructionsOnlySaved({
                            testCaseId: targetTestCaseId,
                            testCaseKey,
                        })
                    );
                    return true;
                }

                dispatch(saveFailed(result?.error || 'Failed to save instructions'));
                showSnackbar('Failed to save instructions', 'error');
                return false;
            } catch (error) {
                dispatch(saveFailed(error.message || 'Save failed'));
                showSnackbar('Failed to save instructions', 'error');
                return false;
            }
        },
        [dispatch, testCaseKey, showSnackbar, store]
    );

    const navigateToSavedTestCase = useCallback(
        (savedTestCaseId) => {
            if (!savedTestCaseId || String(savedTestCaseId) === String(testCaseId)) {
                return;
            }
            markAutomationStudioSessionPreserved();
            navigate(
                generatePath(AUTOMATION_STUDIO_ROUTES.testCase, {
                    project_id: params.project_id,
                    test_case_id: savedTestCaseId,
                }),
                { replace: true }
            );
        },
        [navigate, params.project_id, testCaseId]
    );

    const openLinkTestCaseSavePopup = useCallback(
        (afterSave) => {
            const studioState = store.getState().automationStudioNew;
            const liveInstructions = studioState?.planDraft?.instructions ?? [];
            const liveDynamicResult = studioState?.planDraft?.dynamicResult ?? null;
            const instructionsToSave = resolvePostSessionSaveInstructions(
                studioState.generationRun,
                liveInstructions
            );
            const planToSave = buildPlanForSave(instructionsToSave, liveDynamicResult, testCaseKey);

            onOpenSavePopup?.({
                plan: planToSave,
                instruction: getInstructionPayloadToSave(instructionsToSave),
                onSuccess: async (savedTestCaseId) => {
                    if (!savedTestCaseId) return;
                    dispatch(
                        planSaved({
                            testCaseId: savedTestCaseId,
                            testCaseKey,
                            executionRecordPlan: planToSave,
                        })
                    );
                    showSnackbar('Execution record saved', 'success');
                    if (afterSave) {
                        await afterSave(savedTestCaseId);
                    }
                    navigateToSavedTestCase(savedTestCaseId);
                },
            });
            return SAVE_OPENED_LINK_TEST_CASE_POPUP;
        },
        [
            dispatch,
            navigateToSavedTestCase,
            onOpenSavePopup,
            showSnackbar,
            store,
            testCaseKey,
        ]
    );

    const openLinkTestCaseForInstructionsOnly = useCallback(() => {
        const studioState = store.getState().automationStudioNew;
        const popupData = buildInstructionsOnlyLinkPopupData(studioState);

        onOpenSavePopup?.({
            ...popupData,
            onSuccess: async (savedTestCaseId) => {
                if (!savedTestCaseId) return;
                dispatch(
                    instructionsOnlySaved({
                        testCaseId: savedTestCaseId,
                        testCaseKey,
                    })
                );
                showSnackbar('Instructions saved. Automation plan removed.', 'success');
                navigateToSavedTestCase(savedTestCaseId);
            },
        });
        return SAVE_OPENED_LINK_TEST_CASE_POPUP;
    }, [
        dispatch,
        navigateToSavedTestCase,
        onOpenSavePopup,
        showSnackbar,
        store,
        testCaseKey,
    ]);

    const executeSave = useCallback(async () => {
        if (testCaseId) {
            const saved = await persistSave(testCaseId);
            if (saved) showSnackbar('Execution record saved', 'success');
            return saved;
        }

        return openLinkTestCaseSavePopup();
    }, [testCaseId, persistSave, showSnackbar, openLinkTestCaseSavePopup]);

    const runSaveAndReplay = useCallback(async () => {
        const studioState = store.getState().automationStudioNew;
        const targetId = testCaseId || studioState?.savedPlan?.testCaseId;
        if (!targetId) {
            return openLinkTestCaseSavePopup(async (savedTestCaseId) => {
                await handleReplay(savedTestCaseId);
            });
        }

        const saved = await persistSave(targetId);
        if (saved) {
            showSnackbar('Execution record saved', 'success');
            await handleReplay(targetId);
            return true;
        }
        return false;
    }, [testCaseId, persistSave, handleReplay, showSnackbar, store, openLinkTestCaseSavePopup]);

    const tryOpenFailedRecordingSaveConfirm = useCallback(
        (saveKind) => {
            const { shouldPrompt, failedSteps } = getFailedRecordingStepsForSaveConfirmFromState(
                store.getState().automationStudioNew
            );
            if (!shouldPrompt) {
                return false;
            }
            pendingSaveAfterConfirmRef.current = saveKind;
            onOpenConfirmSavePopup?.({ failedSteps });
            return true;
        },
        [onOpenConfirmSavePopup, store]
    );

    const confirmPendingSaveAfterRecordingFailures = useCallback(async () => {
        const saveKind = pendingSaveAfterConfirmRef.current;
        pendingSaveAfterConfirmRef.current = null;
        if (saveKind === 'instructionsOnly') {
            if (!testCaseId) return false;
            const saved = await persistInstructionsOnly(testCaseId);
            if (saved) {
                showSnackbar('Instructions saved. Automation plan removed.', 'success');
            }
            return saved;
        }
        if (saveKind === 'saveAndReplay') {
            return runSaveAndReplay();
        }
        if (saveKind === 'save') {
            return executeSave();
        }
        return false;
    }, [executeSave, persistInstructionsOnly, runSaveAndReplay, showSnackbar, testCaseId]);

    const cancelPendingSaveAfterRecordingFailures = useCallback(() => {
        pendingSaveAfterConfirmRef.current = null;
    }, []);

    const handleSave = useCallback(async () => {
        trackUiAction(TelemetryActions.SAVE_TESTCASE_CLICKED, {
            projectId,
            testCaseId: testCaseId || undefined,
        });
        const saveChangeKind = selectSaveChangeKind(store.getState());
        const linkSaveAction = resolveInstructionsOnlyLinkSaveAction(saveChangeKind, testCaseId);

        if (linkSaveAction === INSTRUCTIONS_ONLY_LINK_SAVE_ACTION.OPEN_LINK_POPUP) {
            openLinkTestCaseForInstructionsOnly();
            return;
        }

        if (linkSaveAction === INSTRUCTIONS_ONLY_LINK_SAVE_ACTION.CONFIRM_POPUP) {
            pendingSaveAfterConfirmRef.current = 'instructionsOnly';
            onOpenConfirmSavePopup?.({ variant: 'instructionsOnly' });
            return;
        }

        dispatch(setValidationErrors(stepValidation.errorsByStepId));
        if (!isStepValidationValid) {
            showSnackbar(
                'Please fill all required fields: select a tool and mandatory arguments for each step',
                'error'
            );
            return;
        }

        if (tryOpenFailedRecordingSaveConfirm('save')) {
            return;
        }

        await executeSave();
    }, [
        dispatch,
        stepValidation.errorsByStepId,
        isStepValidationValid,
        tryOpenFailedRecordingSaveConfirm,
        executeSave,
        openLinkTestCaseForInstructionsOnly,
        store,
        testCaseId,
        onOpenConfirmSavePopup,
        showSnackbar,
        projectId,
    ]);

    const handleSaveAndReplay = useCallback(async () => {
        trackUiAction(TelemetryActions.SAVE_TESTCASE_CLICKED, {
            projectId,
            testCaseId: testCaseId || undefined,
            saveAndReplay: true,
        });
        dispatch(setValidationErrors(stepValidation.errorsByStepId));
        if (!isStepValidationValid) {
            showSnackbar(
                'Please fill all required fields: select a tool and mandatory arguments for each step',
                'error'
            );
            return;
        }

        if (tryOpenFailedRecordingSaveConfirm('saveAndReplay')) {
            return;
        }

        await runSaveAndReplay();
    }, [
        dispatch,
        stepValidation.errorsByStepId,
        isStepValidationValid,
        tryOpenFailedRecordingSaveConfirm,
        runSaveAndReplay,
        showSnackbar,
        projectId,
        testCaseId,
    ]);

    const handleStopExecution = useCallback(
        (executionId, executionContext) => {
            trackUiAction(TelemetryActions.STOP_AUTOMATION_CLICKED, { projectId });
            const state = store.getState().automationStudioNew;
            if (state.generationRun.status === RUN_STATUS.RUNNING) {
                stopAutomation(executionId || activeRunIdRef.current, executionContext);
                dispatch(generationStopped());
            }
            if (state.replayRun.status === 'running') {
                stopReplayAutomation(executionId || activeRunIdRef.current, executionContext);
                dispatch(replayStopped());
            }
        },
        [dispatch, stopAutomation, stopReplayAutomation, store, projectId]
    );

    return {
        handleGenerate,
        handleReplay,
        handleSave,
        handleSaveAndReplay,
        handleStopExecution,
        confirmPendingSaveAfterRecordingFailures,
        cancelPendingSaveAfterRecordingFailures,
        activeRunIdRef,
        clearAutomationData,
    };
}
